import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewContainerRef,
  WritableSignal
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ZenithComponentsModule } from '@mateco/zenith-webcomponents-angular/dist/zenith-webcomponents-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import firebase from 'firebase/compat';
import { ModalService } from '../../zenith/modal/modal.service';
import { NewFirebaseMessagingService } from '../../../core/services/new-firebase-messaging.service';
import MessagePayload = firebase.messaging.MessagePayload;
import { NotificationModalComponent } from '../../zenith/modal/notification-modal/notification-modal.component';
import { UserService } from '../../../services/user.service';

@Component({
  selector: 'app-firebase-messaging-handler',
  standalone: true,
  imports: [RouterModule, ZenithComponentsModule],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FirebaseMessagingHandlerComponent implements OnInit {
  private readonly newFirebaseMessagingService = inject(NewFirebaseMessagingService);
  private readonly userService = inject(UserService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly router = inject(Router);
  private readonly openModal: WritableSignal<ComponentRef<NotificationModalComponent> | null> = signal(null);
  private readonly messagePayloads: WritableSignal<MessagePayload[]> = signal([]);

  ngOnInit() {
    if (!this.userService.user()!.hasDriverRole()) {
      // Only drivers receive notifications at the moment, no need to request permission and a token for other users (admin and handover only).
      return;
    }

    this.newFirebaseMessagingService.requestPermissionAndSubscribeToken();
    this.newFirebaseMessagingService.messages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((message) => this.onMessage(message));
  }

  private onMessage(message: MessagePayload) {
    if (this.openModal()) {
      this.openModal().destroy();
    }
    this.messagePayloads.set([...this.messagePayloads(), message]);
    const component = this.showNotificationModal(
      this.viewContainerRef,
      () => this.navigateTo(message),
      () => this.destroyModalAndMessagePayloads(),
      this.messagePayloads()
    );
    this.openModal.set(component);
  }

  private showNotificationModal(
    viewContainerRef: ViewContainerRef,
    onConfirm: Function,
    onCancel: Function,
    messagePayloads: MessagePayload[]
  ) {
    const modal = this.modalService.showModal(viewContainerRef, NotificationModalComponent, onConfirm, onCancel);
    modal.setInput('messagePayloads', messagePayloads);
    return modal;
  }

  private navigateTo(message: MessagePayload) {
    this.destroyModalAndMessagePayloads();
    this.router.navigate(['new', 'firebase-messaging-redirect'], { queryParams: { entityType: message.data.entityType } });
  }

  private destroyModalAndMessagePayloads() {
    this.openModal() && this.openModal().destroy();
    this.openModal.set(null);
    this.messagePayloads.set([]);
  }
}
